import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box
} from '@mui/material';

const ActionDialogs = ({
  approveDialogOpen,
  rejectDialogOpen,
  startReviewDialogOpen,
  correctionDialogOpen,
  archiveDialogOpen,
  unarchiveDialogOpen,
  deleteDialogOpen,
  remarks,
  setRemarks,
  archiveReason,
  setArchiveReason,
  unarchiveReason,
  setUnarchiveReason,
  deleteReason,
  setDeleteReason,
  deleteConfirmText,
  setDeleteConfirmText,
  deleteDependencies,
  deletePreviewLoading,
  onCloseApprove,
  onCloseReject,
  onCloseStartReview,
  onCloseCorrection,
  onCloseArchive,
  onCloseUnarchive,
  onCloseDelete,
  onConfirmApprove,
  onConfirmReject,
  onConfirmStartReview,
  onConfirmCorrection,
  onConfirmArchive,
  onConfirmUnarchive,
  onConfirmDelete
}) => {
  const openInNewTab = (url) => {
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // ignore
    }
  };

  return (
    <>
      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={onCloseApprove}>
        <DialogTitle>Approve Technical Log Book Entry</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Remarks (Optional)"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseApprove}>Cancel</Button>
          <Button variant="contained" color="success" onClick={onConfirmApprove}>
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={onCloseReject}>
        <DialogTitle>Reject Technical Log Book Entry</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Rejection remarks are required. The entry will be returned to DRAFT status for editing.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Remarks *"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            required
            error={!remarks.trim()}
            helperText={!remarks.trim() ? 'Please provide rejection remarks' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseReject}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={onConfirmReject}
            disabled={!remarks.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start Review Dialog */}
      <Dialog open={startReviewDialogOpen} onClose={onCloseStartReview}>
        <DialogTitle>Start Review</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Remarks (Optional)"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseStartReview}>Cancel</Button>
          <Button variant="contained" color="info" onClick={onConfirmStartReview}>
            Start Review
          </Button>
        </DialogActions>
      </Dialog>

      {/* Correction Dialog */}
      <Dialog open={correctionDialogOpen} onClose={onCloseCorrection}>
        <DialogTitle>Request Technical Log Book Correction</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Correction remarks are required. The entry will be unlocked for editing and must be re-submitted and re-approved.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Correction Remarks *"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            required
            error={!remarks.trim()}
            helperText={!remarks.trim() ? 'Please provide correction remarks' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseCorrection}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={onConfirmCorrection}
            disabled={!remarks.trim()}
          >
            Request Correction
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Dialog */}
      <Dialog open={archiveDialogOpen} onClose={onCloseArchive}>
        <DialogTitle>Archive Technical Log Book Entry</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Archiving hides this entry from normal lists. Only archived entries can be permanently deleted.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Archive Reason *"
            value={archiveReason}
            onChange={(e) => setArchiveReason(e.target.value)}
            required
            error={!archiveReason.trim()}
            helperText={!archiveReason.trim() ? 'Please provide an archive reason' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseArchive}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={onConfirmArchive}
            disabled={!archiveReason.trim()}
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unarchive Dialog */}
      <Dialog open={!!unarchiveDialogOpen} onClose={onCloseUnarchive}>
        <DialogTitle>Unarchive Technical Log Book Entry</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will restore the entry to normal lists. If the charter already has another active techlog, that charter link will not be moved.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (Optional)"
            value={unarchiveReason || ''}
            onChange={(e) => setUnarchiveReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseUnarchive}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={onConfirmUnarchive}
          >
            Unarchive
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permanent Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={onCloseDelete}>
        <DialogTitle>Permanently Delete Archived Entry</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action is irreversible. The entry must be archived and have no dependencies.
          </Alert>
          {deletePreviewLoading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Loading dependency preview…
            </Alert>
          )}
          {Array.isArray(deleteDependencies) && deleteDependencies.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Related records detected (informational). Permanent deletion will also remove associated Signature/Media rows:
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                {deleteDependencies.map((d, idx) => (
                  <li key={idx}>
                    {d.type}{typeof d.total === 'number' ? ` (total: ${d.total})` : ''}
                    {Array.isArray(d.items) && d.items.length > 0 && (
                      <Box component="ul" sx={{ mt: 0.5, mb: 0.5, pl: 2 }}>
                        {d.items.slice(0, 3).map((item, j) => (
                          <li key={j}>
                            {item._id || '—'}
                            {item.entryType ? ` | ${item.entryType}` : ''}
                            {item.seqNo != null ? ` | seqNo: ${item.seqNo}` : ''}
                            {item.kind ? ` | kind: ${item.kind}` : ''}
                            {item.entityType ? ` | ${item.entityType}` : ''}
                            <Box sx={{ display: 'inline-flex', gap: 1, ml: 1 }}>
                              {d.type === 'Signatures' && item._id && (
                                <Button size="small" variant="text" onClick={() => openInNewTab(`/signatures/${item._id}`)}>
                                  Open
                                </Button>
                              )}
                              {d.type === 'Media' && item._id && (
                                <Button size="small" variant="text" onClick={() => openInNewTab(`/media/${item._id}`)}>
                                  Open
                                </Button>
                              )}
                              {(d.type === 'FlightLogEntry' && (item.sectorIdString || item.sectorId)) && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => openInNewTab(`/flight-log/${item._id}`)}
                                >
                                  Open
                                </Button>
                              )}
                              {(d.type === 'Sector' || d.type === 'Sectors') && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => openInNewTab(`/ops/sectors/${item._id}`)}
                                >
                                  Open
                                </Button>
                              )}
                            </Box>
                          </li>
                        ))}
                      </Box>
                    )}
                  </li>
                ))}
              </Box>
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Delete Reason *"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            required
            error={!deleteReason.trim()}
            helperText={!deleteReason.trim() ? 'Please provide a delete reason' : ''}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Type DELETE to confirm *"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            required
            error={deleteConfirmText.trim() !== 'DELETE'}
            helperText={deleteConfirmText.trim() !== 'DELETE' ? 'Confirmation text must be DELETE' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseDelete}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={onConfirmDelete}
            disabled={!deleteReason.trim() || deleteConfirmText.trim() !== 'DELETE'}
          >
            Permanently Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ActionDialogs;

